import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useTranslation } from '../../../hooks/useTranslation';
import { useNavigate } from 'react-router-dom';

const SugarTruckConfirmModal = ({ load, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedQuantity, setSelectedQuantity] = useState(load?.quantity || 0);
  const [insuranceAdded, setInsuranceAdded] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [quantityError, setQuantityError] = useState('');

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date?.getTime())) return '—';
    return date?.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const handleQuantityChange = (e) => {
    const value = parseFloat(e?.target?.value) || 0;
    setQuantityError('');
    
    if (value <= 0) {
      setQuantityError('Quantity must be greater than 0');
    } else if (value > load?.quantity) {
      setQuantityError(`Maximum available quantity is ${load?.quantity} MT`);
    } else {
      setSelectedQuantity(value);
    }
  };

  const handleProceedToReview = () => {
    if (selectedQuantity <= 0) {
      setQuantityError('Please enter a valid quantity');
      return;
    }
    if (selectedQuantity > load?.quantity) {
      setQuantityError(`Maximum available quantity is ${load?.quantity} MT`);
      return;
    }
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  // Calculate amounts with selected quantity
  const subtotal = load?.rate * selectedQuantity;
  const gstRate = 0.05; // 5% GST for sugar
  const gstAmount = Math.round(subtotal * gstRate);
  const transitInsuranceRate = 0.005; // 0.5% insurance
  const transitInsuranceAmount = Math.round(subtotal * transitInsuranceRate);
  
  // Use transportation rate from transporter data (rate per ton) multiplied by tonnage
  const transportationRatePerTon = load?.transportationRatePerTon || 1500; // Fallback to 1500 if not provided
  const truckingCost = Math.round(selectedQuantity * transportationRatePerTon);
  
  // Approx rate per kg converted from rate per ton
  const approxRatePerKg = transportationRatePerTon / 1000;

  const totalAmount = subtotal + gstAmount + truckingCost + (insuranceAdded ? transitInsuranceAmount : 0);

  const handleInsuranceToggle = () => {
    setInsuranceAdded(!insuranceAdded);
  };

  const handleConfirm = () => {
    if (!agreedToTerms) return;
    onConfirm({ ...load, selectedQuantity, insuranceAdded, totalAmount, bundleType: 'sugar+truck' });
  };

  // Calculate required number of trucks (up to 35 MT per truck)
  const requiredTrucks = Math.ceil(selectedQuantity / 35);

  // Mock truck details for display
  const availableTruckTypes = [
    { type: 'Tata LPT 2518', capacity: 'Up to 35 MT', count: Math.min(requiredTrucks, load?.trucksAvailable) },
    { type: 'Ashok Leyland 2518', capacity: 'Up to 35 MT', count: Math.max(0, Math.min(requiredTrucks - load?.trucksAvailable, load?.trucksAvailable)) }
  ]?.filter(truck => truck?.count > 0);

  return (
    <>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[1400] flex items-center justify-center p-4 animate-fadeIn">
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-4xl w-full border-2 border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-8 py-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">
                  {step === 1 ? 'Select Quantity - Sugar+Truck' : 'Review Bundle Purchase'}
                </h2>
                <p className="text-sm text-green-100 mt-1">
                  {step === 1 ? 'Choose how much sugar you want to purchase with trucks' : 'Confirm your Sugar+Truck bundle details'}
                </p>
              </div>
              <button
                onClick={onClose}
                className="w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-all duration-200 text-white text-xl font-bold"
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-8 max-h-[calc(90vh-200px)] overflow-y-auto">
            {step === 1 ? (
              <div className="space-y-6">
                {/* Product Info */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4">Bundle Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Mill Name:</span>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">{load?.millName}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Offer ID:</span>
                      <p className="text-base font-semibold text-green-600 dark:text-green-400">{load?.offerId}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Product:</span>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">{load?.product}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Grade:</span>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">{load?.grade}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Rate per Ton:</span>
                      <p className="text-base font-bold text-green-600 dark:text-green-400">{formatCurrency(load?.rate)}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Available:</span>
                      <p className="text-base font-bold text-gray-900 dark:text-white">{load?.quantity} MT</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Location:</span>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">{load?.deliveryFrom}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Transport Rate (per Ton):</span>
                      <p className="text-base font-bold text-blue-600 dark:text-blue-400">{formatCurrency(transportationRatePerTon)}/Ton</p>
                    </div>
                    <div className="col-span-2 rounded-xl p-3" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold" style={{ color: '#92400e' }}>Approx Rate per Kg (Transport)</span>
                        <span className="text-base font-bold" style={{ color: '#d97706' }}>₹{approxRatePerKg?.toFixed(2)}/Kg</span>
                      </div>
                      <p className="text-xs mt-1.5" style={{ color: '#b45309' }}>
                        Final transport rate and exact distance will be confirmed after booking with the transporter.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quantity Input */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-base font-bold text-gray-900 dark:text-white mb-3">
                      Enter Quantity (MT)
                    </label>
                    <Input
                      type="number"
                      min="0"
                      max={load?.quantity}
                      step="0.1"
                      value={selectedQuantity}
                      onChange={handleQuantityChange}
                      placeholder="Enter quantity in metric tons"
                      className="text-lg font-semibold h-14 px-5 border-2"
                    />
                    {quantityError && (
                      <p className="text-sm text-red-600 dark:text-red-400 mt-2 font-semibold">
                        ⚠ {quantityError}
                      </p>
                    )}
                  </div>

                  {/* Quick Select Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <span className="text-sm text-gray-600 dark:text-gray-400 font-semibold mr-2 self-center">Quick Select:</span>
                    {[25, 50, 100, load?.quantity]?.map((qty) => (
                      qty <= load?.quantity && (
                        <Button
                          key={qty}
                          size="sm"
                          onClick={() => {
                            setSelectedQuantity(qty);
                            setQuantityError('');
                          }}
                          className="text-sm font-semibold h-10 px-5 border-2 bg-black text-white hover:bg-gray-800 border-black"
                        >
                          {qty} MT
                        </Button>
                      )
                    ))}
                  </div>

                  {/* Estimated Amount Preview */}
                  {selectedQuantity > 0 && !quantityError && (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-300 dark:border-green-700 rounded-2xl p-5 animate-fadeIn shadow-sm">
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-base text-gray-700 dark:text-gray-300 font-semibold">Sugar Cost:</span>
                          <span className="text-xl font-bold text-gray-900 dark:text-white">{formatCurrency(load?.rate * selectedQuantity)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="text-base text-gray-700 dark:text-gray-300 font-semibold">Transportation:</span>
                            <span className="text-xs text-gray-600 dark:text-gray-400 block">{formatCurrency(transportationRatePerTon)}/Ton · Approx ₹{approxRatePerKg?.toFixed(2)}/Kg</span>
                          </div>
                          <span className="text-xl font-bold text-gray-900 dark:text-white">{formatCurrency(truckingCost)}</span>
                        </div>
                        <div className="flex justify-between items-center pt-3 border-t-2 border-green-300 dark:border-green-700">
                          <span className="text-base text-gray-700 dark:text-gray-300 font-semibold">Required Trucks:</span>
                          <span className="text-xl font-bold text-green-600 dark:text-green-400">{Math.ceil(selectedQuantity / 35)} Units</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-4">
                        {selectedQuantity} MT × {formatCurrency(load?.rate)}/MT + ({selectedQuantity} MT × {formatCurrency(transportationRatePerTon)}/Ton) excluding taxes
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Truck Capacity (Up to 35 Ton) — Average Truck Capacity 25T to 35T
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Bundle Summary */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-6 border-2 border-green-200 dark:border-green-800 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <span className="text-2xl">📦</span> Bundle Summary
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Mill Name:</span>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">{load?.millName}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Offer ID:</span>
                      <p className="text-base font-semibold text-green-600 dark:text-green-400">{load?.offerId}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Product:</span>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">{load?.product} ({load?.grade})</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Quantity:</span>
                      <p className="text-base font-bold text-green-600 dark:text-green-400">{selectedQuantity} MT</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Location:</span>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">{load?.deliveryFrom}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Required Trucks:</span>
                      <p className="text-base font-bold text-green-600 dark:text-green-400">{requiredTrucks} Units</p>
                    </div>
                  </div>
                </div>

                {/* Truck Details */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-6 border-2 border-blue-200 dark:border-blue-800 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <span className="text-2xl">🚛</span> Truck Details
                  </h3>
                  <div className="space-y-3">
                    {availableTruckTypes?.map((truck, index) => (
                      <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-base font-semibold text-gray-900 dark:text-white">{truck?.type}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Capacity: {truck?.capacity}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{truck?.count} Units</p>
                            <p className="text-xs text-gray-600 dark:text-gray-400">Assigned</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-lg p-3 mt-3">
                      <p className="text-sm text-green-800 dark:text-green-300 font-semibold">
                        ✓ Truck Capacity (Up to 35 Ton) — Average Truck Capacity 25T to 35T
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pricing Breakdown */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-6 border-2 border-gray-200 dark:border-gray-700 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <span className="text-2xl">💰</span> Complete Pricing
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-3 border-b border-gray-300 dark:border-gray-600">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Sugar Cost ({selectedQuantity} MT):</span>
                      <span className="text-base font-semibold text-gray-900 dark:text-white">{formatCurrency(subtotal)}</span>
                    </div>
                    <div className="pb-3 border-b border-gray-300 dark:border-gray-600">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-sm text-gray-600 dark:text-gray-400">Transportation ({requiredTrucks} trucks):</span>
                          <span className="text-xs text-gray-500 dark:text-gray-500 block mt-1">
                            Rate: {formatCurrency(transportationRatePerTon)}/Ton × {selectedQuantity} MT · Approx ₹{approxRatePerKg?.toFixed(2)}/Kg · Avg Capacity 25T–35T
                          </span>
                        </div>
                        <span className="text-base font-semibold text-gray-900 dark:text-white">{formatCurrency(truckingCost)}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-blue-600 dark:text-blue-400 pb-3 border-b border-gray-300 dark:border-gray-600">
                      <span className="text-sm font-semibold">GST (5% on sugar):</span>
                      <span className="text-sm font-semibold">+{formatCurrency(gstAmount)}</span>
                    </div>
                    
                    {/* Insurance Toggle */}
                    {load?.insuranceAvailable && (
                      <div className="pb-3 border-b border-gray-300 dark:border-gray-600">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm text-gray-600 dark:text-gray-400 font-semibold">
                            Transit Insurance (0.5%):
                          </span>
                          <Button
                            size="sm"
                            onClick={handleInsuranceToggle}
                            className="text-sm h-10 px-4 font-semibold border-2 bg-black text-white hover:bg-gray-800 border-black"
                          >
                            {insuranceAdded ? '✓ Added' : '+ Add'}
                          </Button>
                        </div>
                        {insuranceAdded && (
                          <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-300 dark:border-green-700 rounded-xl p-3 text-sm animate-fadeIn">
                            <div className="flex justify-between items-center text-green-700 dark:text-green-400">
                              <span className="font-semibold">Insurance Premium:</span>
                              <span className="font-bold">+{formatCurrency(transitInsuranceAmount)}</span>
                            </div>
                            <p className="text-xs text-green-600 dark:text-green-500 mt-2">Coverage: ₹25L+ per consignment</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Approx Rate per Kg note */}
                    <div className="rounded-xl p-3" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-semibold" style={{ color: '#92400e' }}>Approx Rate per Kg (Transport)</span>
                        <span className="text-base font-bold" style={{ color: '#d97706' }}>₹{approxRatePerKg?.toFixed(2)}/Kg</span>
                      </div>
                      <p className="text-xs" style={{ color: '#b45309' }}>
                        Final transport rate and exact distance will be confirmed after booking with the transporter.
                      </p>
                    </div>
                    
                    <div className="flex justify-between items-center pt-4 border-t-2 border-gray-400 dark:border-gray-600">
                      <span className="text-lg font-bold text-gray-900 dark:text-white">Total Bundle Cost:</span>
                      <span className="text-3xl font-bold text-green-600 dark:text-green-400">{formatCurrency(totalAmount)}</span>
                    </div>
                  </div>
                </div>

                {/* Important Dates */}
                <div className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-2xl p-5 shadow-sm">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <span className="text-xl">📅</span> Key Dates
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-amber-800 dark:text-amber-300">Payment Date:</span>
                      <p className="text-base font-bold text-amber-900 dark:text-amber-100">{formatDate(load?.paymentDate)}</p>
                    </div>
                    <div>
                      <span className="text-sm text-amber-800 dark:text-amber-300">Lifting Date:</span>
                      <p className="text-base font-bold text-amber-900 dark:text-amber-100">{formatDate(load?.liftingDate)}</p>
                    </div>
                  </div>
                </div>

                {/* Payment Terms */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-300 dark:border-blue-700 rounded-2xl p-5 shadow-sm">
                  <div className="text-sm text-blue-900 dark:text-blue-100">
                    <p className="font-bold mb-3 text-base flex items-center gap-2">
                      <span className="text-xl">💳</span> Payment Terms - Sugar+Truck Bundle
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 dark:text-blue-400 font-bold">•</span>
                        <span><strong>Advance payment at booking</strong> — secured until delivery confirmation</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 dark:text-blue-400 font-bold">•</span>
                        <span><strong>Balance payment after invoice generation</strong> — due upon invoice issuance</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 dark:text-blue-400 font-bold">•</span>
                        <span><strong>Full payment before vehicle dispatch from factory gate</strong> — vehicle will not leave until full payment is confirmed</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 dark:text-blue-400 font-bold">•</span>
                        <span><strong>100% Secure Factory-Direct Transactions</strong> — payment on delivery confirmation</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Terms Agreement */}
                <div className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-5 border-2 border-gray-300 dark:border-gray-700">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e?.target?.checked)}
                      className="mt-1 w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                      I agree to the payment terms. I understand advance payment will be paid now and the balance after invoice. Full payment is required before vehicle leaves the factory gate.
                    </span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="bg-gray-50 dark:bg-gray-900 border-t-2 border-gray-200 dark:border-gray-700 px-8 py-5">
            <div className="flex justify-end gap-4">
              {step === 1 ? (
                <>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={onClose}
                    className="min-w-[120px] h-13 px-6 py-3 text-base font-bold border-2 border-gray-400 text-gray-600 dark:text-gray-300 dark:border-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 bg-transparent"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    size="lg"
                    onClick={handleProceedToReview}
                    className="min-w-[200px] h-13 px-8 py-3 text-base font-extrabold bg-amber-400 hover:bg-amber-500 text-gray-900"
                    disabled={!selectedQuantity || selectedQuantity <= 0 || !!quantityError}
                  >
                    Proceed to Review →
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handleBack}
                    className="min-w-[120px] h-13 px-6 py-3 text-base font-bold border-2 border-gray-400 text-gray-600 dark:text-gray-300 dark:border-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 bg-transparent"
                  >
                    ← Back
                  </Button>
                  <Button
                    variant="default"
                    size="lg"
                    onClick={handleConfirm}
                    disabled={!agreedToTerms}
                    className="min-w-[220px] h-13 px-8 py-3 text-base font-extrabold shadow-lg hover:shadow-xl transition-all duration-200 bg-amber-400 hover:bg-amber-500 text-gray-900 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                  >
                    ✓ Confirm Bundle Purchase
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SugarTruckConfirmModal;